import { getCookies } from "cookies-next/server";

import getUser from "api/requests/getUser";
import getUserIdCards from "api/requests/getUserIdCards";
import { getUserLocationData } from "api/requests/getUserLocation";
import getUserMedCard from "api/requests/getUserMedCard";
import { loadCheckoutDisclaimerThunk } from "redux/action-creators/cartDispensary";
import { useSelector } from "redux/hooks";
import { SET_USER_DELIVERY_ADDRESS } from "redux/reducers/location";
import {
  USER_LOAD,
  USER_LOAD_ID_CARDS_FAILURE,
  USER_LOAD_ID_CARDS_REQUEST,
  USER_LOAD_ID_CARDS_SUCCESS,
  USER_LOAD_MED_CARD,
} from "redux/reducers/user";
import {
  getCartContext,
  getCartItems,
  getDealGroups,
} from "redux/selectors/cart";
import { getCartDispensaryData } from "redux/selectors/cartDispensary";
import { getDomainCountryCode } from "redux/selectors/config";
import { getIsLoggedIn } from "redux/selectors/user";
import { createGetServerSideProps } from "utils/createGetServerSideProps";
import getCartAndDispensary from "utils/getCartAndDispensary";
import { normalizeLocationCookie } from "utils/getUserLocationFromCookie";
import shouldUseJaneCheckout from "utils/shouldUseJaneCheckout";

import Bag from "components/Checkout/Bag";
import CheckoutLayout from "components/Checkout/CheckoutLayout";
import { JaneCheckout } from "components/Checkout/JaneCheckout";

type BagPageProps = {
  referrer: string | null;
  menuType: string | null;
  isAndroidNative: boolean;
};

const BagPage: React.FC<BagPageProps> = ({
  referrer,
  menuType,
  isAndroidNative,
}) => {
  const cartItems = useSelector(getCartItems);
  const dealGroups = useSelector(getDealGroups);
  const dispensary = useSelector(getCartDispensaryData);

  const isJaneCheckoutEnabled = shouldUseJaneCheckout(dispensary);

  return (
    <CheckoutLayout
      className="m-none md:m-auto"
      indexInSearchEngines={true}
      isAndroidNative={isAndroidNative}
      menuType={menuType}
      referrer={referrer}
    >
      {isJaneCheckoutEnabled ? (
        <div className="container">
          <JaneCheckout cartItems={cartItems} />
        </div>
      ) : (
        <Bag cartItems={cartItems} dealGroups={dealGroups} />
      )}
    </CheckoutLayout>
  );
};

export const getServerSideProps = createGetServerSideProps<BagPageProps>(
  async (context) => {
    const { store, req, res, query } = context;

    const cookies = await getCookies({ req, res });

    if (req) {
      store.dispatch({
        forDelivery: query.forDelivery === "true",
        type: "SET_FOR_DELIVERY_OPTION",
      });
      const domainCountryCode = getDomainCountryCode(store.getState());

      /** Look for 'leafly-location' cookie and if it exists,
       *  set deliveryAddress state */
      // TODO: figure out why cookie is being reset on page refresh
      const userLocationData = await getUserLocationData(
        domainCountryCode,
        context,
      );
      const location = normalizeLocationCookie(userLocationData);

      if (location) {
        store.dispatch({
          address: location,
          type: SET_USER_DELIVERY_ADDRESS,
        });
      }
      const cartContext = getCartContext(store.getState());
      const cartId = query?.cartId || cookies["X-Leafly-Id"] || null;

      const [_, user] = await Promise.all([
        getCartAndDispensary({
          cartContext: cartContext,
          cartId: cartId as string,
          dispatch: store.dispatch,
          domainCountryCode: domainCountryCode,
          request: req,
          updateOnLoad: query.refresh === "1",
        }),
        getUser(domainCountryCode, req?.headers.cookie),
      ]);

      store.dispatch({
        type: USER_LOAD,
        userData: user,
      });

      store.dispatch(loadCheckoutDisclaimerThunk());

      // * Fetch med id info for logged in users.
      // ! This is used in /checkout, not /bag, but as long as we're doing a client-side transition from /bag to /checkout, many checkout related properties must be loaded here.
      store.dispatch({
        type: USER_LOAD_ID_CARDS_REQUEST,
      });

      const [cardData, medCard] = await Promise.all([
        getUserIdCards({ cookies, countryCode: domainCountryCode }),
        getIsLoggedIn(store.getState()) && !store.getState()?.user?.medCard
          ? getUserMedCard({ cookies, domainCountryCode })
          : undefined,
      ]);

      if (medCard && !("error" in medCard)) {
        store.dispatch({
          medCard,
          type: USER_LOAD_MED_CARD,
        });
      }
      if (!("error" in cardData)) {
        store.dispatch({
          cardData,
          type: USER_LOAD_ID_CARDS_SUCCESS,
        });
      } else {
        store.dispatch({
          type: USER_LOAD_ID_CARDS_FAILURE,
        });
      }
      // * End of server side only code
    }

    return {
      props: {
        disableCheckoutBanner: true,
        disableGoogleMaps: true,
        isAndroidNative:
          req?.headers.referer === "android-app://leafly.android",
        menuType: Array.isArray(query.menuType)
          ? query.menuType[0]
          : query.menuType || null,
        referrer: Array.isArray(query.referrer)
          ? query.referrer[0]
          : query.referrer || null,
        showMinimalHeader: true,
      },
    };
  },
);

export default BagPage;
